// src/hooks/useFolders.js
// ============================================================================
// Folder data hook + CRUD — Programs library organization (pass 1).
//
// PERSISTENCE ONLY. This hook owns the `folders` collection and program->folder
// writes. It contains NO Programs-page UI, NO selected-folder state, NO filtering
// semantics, NO Builder logic — consumers own presentation. (Mirrors useAssets:
// a realtime owner-scoped subscription + house-style auth/error handling.)
//
// Schema (folders/{folderId}): { name, userId, parentFolderId, createdAt, updatedAt }
//   - doc.id is the id (no inner id field, matches programs/assets convention)
//   - parentFolderId: null = top-level; a folderId = child (ONE level only)
//   - userId: Firebase UID owner (same convention as programs/playlists/assets)
//
// APPLICATION-ENFORCED INVARIANTS (NOT database-guaranteed):
//   - Two levels only: a child (parentFolderId != null) cannot receive children.
//   - Delete blocked if non-empty (child folders OR programs) — checked immediately
//     at delete execution, not at modal-open, to avoid stale-state orphaning.
//   - "Unfiled" is virtual (programs with folderId == null) — never a folder doc.
//   - Ownership: EVERY mutating CRUD fn takes currentUser and verifies the target is
//     owned before writing (assertFolderOwned / program userId check). This is a
//     client-side pre-check for uniform behavior + friendly errors; the production
//     Firestore rules are the actual security boundary. Consistent across all four ops.
// Firestore rules do NOT validate depth, parent validity, or folderId referential
// integrity in v1; this hook is the enforcement point.
// ============================================================================

import { useState, useEffect } from 'react';
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import devLog from '../utils/devLog';

// ---------------------------------------------------------------------------
// useFolders — realtime owner-scoped subscription to the folders collection.
// Returns { folders, loading }. `folders` is the flat list of the user's folder
// docs; consumers build the 2-level tree from parentFolderId. (Mirrors useAssets'
// onSnapshot + isAdmin/own filter shape.)
// ---------------------------------------------------------------------------
export function useFolders() {
  const { currentUser, isAdmin } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(
      collection(db, 'folders'),
      (snapshot) => {
        devLog('Firestore update received:', snapshot.docs.length, 'folders');
        const foldersData = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((f) => {
            // Same own+shared/own filter shape as programs/assets.
            if (isAdmin) return !f.userId || f.userId === currentUser?.uid;
            return f.userId === currentUser?.uid;
          });
        // Alphabetical by name for a stable tree.
        foldersData.sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
        );
        setFolders(foldersData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching folders:', error);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [currentUser, isAdmin]);

  return { folders, loading };
}

// ---------------------------------------------------------------------------
// CRUD helpers — plain async functions (not part of the hook's render cycle).
// House convention: throw on failure so the caller can catch + surface; also
// console.error for visibility. No toast system exists in these files, so we
// don't invent one — callers decide how to present errors.
// ---------------------------------------------------------------------------

/**
 * Shared ownership guard: throws unless the folder exists and is owned by currentUser.
 * Client-side pre-check for a clean error + uniform CRUD ownership (production Firestore
 * rules are the real boundary; this keeps the surface consistent and errors friendly).
 */
async function assertFolderOwned(folderId, currentUser) {
  const snap = await getDoc(doc(db, 'folders', folderId));
  if (!snap.exists()) throw new Error('folder not found');
  if (snap.data().userId !== currentUser.uid) throw new Error('folder not owned by user');
}

/**
 * Validate a destination folder is structurally valid for filing a Program:
 * it must exist + be owned, and if it's a child, its parent must exist, be owned,
 * and be top-level. This enforces the two-level invariant AT THE POINT OF FILING —
 * a Program can only land in a valid top-level or valid child folder, never an
 * accidental deeper node created by an old/malformed client (the contract says the
 * application is the enforcement point since Firestore does not guarantee this).
 * @throws Error if the destination is missing, not owned, or structurally invalid.
 */
async function assertValidDestinationFolder(folderId, currentUser) {
  const snap = await getDoc(doc(db, 'folders', folderId));
  if (!snap.exists()) throw new Error('destination folder not found');
  const f = snap.data();
  if (f.userId !== currentUser.uid) throw new Error('destination folder not owned by user');
  if (f.parentFolderId != null) {
    // It's a child — its parent must exist, be owned, and be top-level.
    const parentSnap = await getDoc(doc(db, 'folders', f.parentFolderId));
    if (!parentSnap.exists()) throw new Error('destination folder has an invalid parent');
    const parent = parentSnap.data();
    if (parent.userId !== currentUser.uid) throw new Error('destination folder parent not owned by user');
    if (parent.parentFolderId != null) {
      throw new Error('destination folder is nested too deep (invalid hierarchy)');
    }
  }
}

/**
 * Create a folder. parentId=null -> top-level. parentId=<topLevelFolderId> -> child.
 * Enforces the two-level rule: the parent (if any) must itself be top-level.
 * @throws Error if not authed, name empty, parent missing/not-owned, or parent is a child.
 */
export async function createFolder(name, parentId, currentUser) {
  const trimmed = (name || '').trim();
  if (!currentUser?.uid) throw new Error('createFolder: not authenticated');
  if (!trimmed) throw new Error('createFolder: name is required');

  if (parentId) {
    // Two-level enforcement: the parent must exist, be owned, and be top-level.
    const parentSnap = await getDoc(doc(db, 'folders', parentId));
    if (!parentSnap.exists()) throw new Error('createFolder: parent folder not found');
    const parent = parentSnap.data();
    if (parent.userId !== currentUser.uid) throw new Error('createFolder: parent not owned by user');
    if (parent.parentFolderId != null) {
      throw new Error('createFolder: cannot nest under a child folder (two levels max)');
    }
  }

  try {
    const ref = await addDoc(collection(db, 'folders'), {
      name: trimmed,
      userId: currentUser.uid,
      parentFolderId: parentId || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return ref.id;
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
}

/**
 * Rename a folder. Owner-only (also enforced by rules). userId/parentFolderId untouched.
 * @throws Error if name empty or the write fails.
 */
export async function renameFolder(folderId, name, currentUser) {
  const trimmed = (name || '').trim();
  if (!currentUser?.uid) throw new Error('renameFolder: not authenticated');
  if (!trimmed) throw new Error('renameFolder: name is required');
  await assertFolderOwned(folderId, currentUser);
  try {
    await updateDoc(doc(db, 'folders', folderId), {
      name: trimmed,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error renaming folder:', error);
    throw error;
  }
}

/**
 * Delete a folder — ONLY if empty. Emptiness (child folders OR programs) is checked
 * IMMEDIATELY here, at execution, against fresh data — not at modal-open — so a program
 * filed in between can't be silently orphaned. No cascade in v1.
 * @throws Error 'FOLDER_NOT_EMPTY' if it has children or programs; or on write failure.
 */
export async function deleteFolder(folderId, currentUser) {
  if (!currentUser?.uid) throw new Error('deleteFolder: not authenticated');
  await assertFolderOwned(folderId, currentUser);
  try {
    // 1) child folders?
    const childrenSnap = await getDocs(
      query(collection(db, 'folders'), where('parentFolderId', '==', folderId))
    );
    if (!childrenSnap.empty) {
      const e = new Error('FOLDER_NOT_EMPTY: folder contains child folders');
      e.code = 'FOLDER_NOT_EMPTY';
      throw e;
    }
    // 2) programs filed here?
    const programsSnap = await getDocs(
      query(collection(db, 'programs'), where('folderId', '==', folderId))
    );
    if (!programsSnap.empty) {
      const e = new Error('FOLDER_NOT_EMPTY: folder contains programs');
      e.code = 'FOLDER_NOT_EMPTY';
      throw e;
    }
    // Empty -> safe to delete.
    await deleteDoc(doc(db, 'folders', folderId));
  } catch (error) {
    if (error.code !== 'FOLDER_NOT_EMPTY') console.error('Error deleting folder:', error);
    throw error;
  }
}

/**
 * Move a program into a folder (or to Unfiled). This is the ONLY program-side write
 * folders introduce — a single folderId update. folderId=null => Unfiled.
 * Does NOT touch composition/theme/runtime.
 * @throws Error on write failure.
 */
export async function moveProgramToFolder(programId, folderId, currentUser) {
  if (!currentUser?.uid) throw new Error('moveProgramToFolder: not authenticated');
  // Verify the program is owned by the caller.
  const progSnap = await getDoc(doc(db, 'programs', programId));
  if (!progSnap.exists()) throw new Error('moveProgramToFolder: program not found');
  if (progSnap.data().userId !== currentUser.uid) {
    throw new Error('moveProgramToFolder: program not owned by user');
  }
  // If filing into a folder, verify ownership + valid two-level structure.
  if (folderId) await assertValidDestinationFolder(folderId, currentUser);
  try {
    await updateDoc(doc(db, 'programs', programId), {
      folderId: folderId || null,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error moving program to folder:', error);
    throw error;
  }
}